import { useQuery, useQueryClient } from '@tanstack/react-query';
import { loadAllConfigData, EMPTY_CONFIG } from '../services/configDataService';

export const SYSTEM_CONFIG_KEY = ['system-settings', 'all-config'];

// ─── Data fetching hook ───────────────────────────────────────────────────────
export const useSystemConfig = () => {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: SYSTEM_CONFIG_KEY,
    queryFn: loadAllConfigData,
    staleTime: 5 * 60 * 1000,       // treat as fresh for 5 minutes
    placeholderData: EMPTY_CONFIG,   // render immediately with empty arrays
    retry: (failureCount, err) => {
      if (err?.message?.includes('Authentication')) return false;
      return failureCount < 2;
    },
  });

  return {
    configData: data ?? EMPTY_CONFIG,
    isLoading,
    isError,
    error,
    refetch,
  };
};

// ─── Cache mutation helpers (replaces setConfigData imperatives) ──────────────
export const useSystemConfigMutations = () => {
  const queryClient = useQueryClient();

  const addItem = (category, item) => {
    queryClient.setQueryData(SYSTEM_CONFIG_KEY, (old) => ({
      ...old,
      [category]: [...(old?.[category] ?? []), item],
    }));
  };

  const updateItem = (category, updatedItem) => {
    queryClient.setQueryData(SYSTEM_CONFIG_KEY, (old) => ({
      ...old,
      [category]: (old?.[category] ?? []).map((i) =>
        i.id === updatedItem.id ? updatedItem : i
      ),
    }));
  };

  const removeItem = (category, itemId) => {
    queryClient.setQueryData(SYSTEM_CONFIG_KEY, (old) => ({
      ...old,
      [category]: (old?.[category] ?? []).filter((i) => i.id !== itemId),
    }));
  };

  return { addItem, updateItem, removeItem };
};
